import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, Clock, MapPin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV, PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";

const TopBar = () => (
  <div className="hidden md:block bg-[var(--navy-deep)] text-white/80 text-xs">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 h-9 flex items-center justify-between">
      <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-[var(--green-bright)]" /> Property maintenance throughout Oxfordshire</span>
      <span className="flex items-center gap-6">
        <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-[var(--green-bright)]" /> Mon–Sat 8:00 AM – 6:00 PM</span>
        <a href={PHONE_TEL} data-testid="topbar-phone-link" className="font-semibold text-white link-underline">{PHONE_DISPLAY}</a>
      </span>
    </div>
  </div>
);

const linkCls = ({ isActive }) =>
  `relative px-3 py-2 text-[15px] font-medium transition-colors duration-300 ${isActive ? "text-[var(--navy)]" : "text-slate-600 hover:text-[var(--navy)]"}`;

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="lg:hidden absolute inset-x-0 top-full bg-white border-b border-slate-200 shadow-xl"
    data-testid="mobile-menu"
  >
    <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
      {NAV.map((n) => (
        <NavLink key={n.path} to={n.path} end onClick={onClose} data-testid={`mobile-nav-link-${n.id}`}
          className={({ isActive }) => `font-display text-2xl py-2 ${isActive ? "text-[var(--green)]" : "text-[var(--navy)]"}`}>
          {n.name}
        </NavLink>
      ))}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <a href={PHONE_TEL} data-testid="mobile-menu-call" className="flex items-center justify-center gap-2 rounded-full border border-[var(--navy)] py-3 font-semibold text-[var(--navy)]">
          <Phone className="h-4 w-4" /> Call us
        </a>
        <Link to="/quote" onClick={onClose} data-testid="mobile-menu-quote" className="flex items-center justify-center rounded-full bg-[var(--green)] py-3 font-semibold text-white">
          Free quote
        </Link>
      </div>
    </nav>
  </motion.div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50">
      <TopBar />
      <div className="relative bg-white/90 backdrop-blur-xl border-b border-slate-200/80">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 h-[72px] flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.path} to={n.path} end className={linkCls} data-testid={`nav-link-${n.id}`}>
                {({ isActive }) => (
                  <>
                    {n.name}
                    {isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-[var(--green)]" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a href={PHONE_TEL} data-testid="nav-phone-button" className="hidden xl:flex items-center gap-2 px-3 py-2 text-sm font-semibold text-[var(--navy)] hover:text-[var(--green)] transition-colors">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
            <Link to="/quote" data-testid="nav-quote-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-[var(--green)] px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[var(--green-hover)] hover:-translate-y-0.5">
              Get a free quote
            </Link>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-expanded={open} aria-label={open ? "Close menu" : "Open menu"}
              className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-[var(--navy)]">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
      </div>
    </header>
  );
};
